<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue'
import { NTextarea } from '@novonordisk/studyhub-novoui/vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@/utilities/validators.ts'
import { useValidationMessages } from '@/composables/useValidationMessages.ts'
import { feedbackService } from '@/services/FeedbackService.ts'
import { captureException } from '@sentry/vue'
import { useErrorStore } from '@/store/error.ts'
import { useToastStore } from '@/store/toast.ts'

const modelValue = defineModel<boolean>('modelValue', { type: Boolean, default: false })

function open() {
  modelValue.value = true
}

function close() {
  modelValue.value = false
}

const subjectOptions = [
  {
    text: 'General Feedback',
    value: 'general',
  },
  {
    text: 'Ideas for Improvement',
    value: 'improvement',
  },
  {
    text: 'Bug Report',
    value: 'bug',
  },
]

const isOpen = computed(() => modelValue.value)
const loading = ref<boolean>(false)

export type Form = {
  category: 'general' | 'improvement' | 'bug' | ''
  feedback: string
}

const form = reactive<Form>({
  category: 'general',
  feedback: '',
})

const v$ = useVuelidate(
  {
    category: {
      required,
    },
    feedback: {
      required,
    },
  },
  form,
)

const { errorMessages } = useValidationMessages(v$)
const { showError } = useErrorStore()
const { addToast } = useToastStore()

async function submit() {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  loading.value = true

  try {
    await feedbackService.submitFeedback(form)
    loading.value = false
    modelValue.value = false

    addToast({
      variant: 'success',
      title: 'Feedback submitted',
      text: 'Thank you for your feedback!',
    })
  } catch (err) {
    captureException(err)
    showError()
  }
  loading.value = false
}

watch(modelValue, newValue => {
  if (newValue) {
    form.category = 'general'
    form.feedback = ''
    v$.value.$reset()
  }
})
</script>

<template>
  <div>
    <slot
      name="activator"
      v-bind="{ open, close, isOpen }"
    />
    <VDialog
      v-model="modelValue"
      :close-on-click-outside="!loading"
    >
      <VDialogCard class="w-[500px]">
        <template #title>
          <h2 class="text-h6">Provide Feedback</h2>
        </template>
        <template #default>
          <p class="mb-2">Help us improve country allocation</p>
          <p class="mb-4">
            Whether you found something you liked, came across an error, or are missing information, we want to hear
            from you.
          </p>

          <VSelect
            v-model="form!.category"
            :error-messages="errorMessages['category'] ? [errorMessages['category']] : []"
            :items="subjectOptions"
            class="mb-4"
            data-test="feedback-category"
            disable-search
            item-text="text"
            item-value="value"
          />
          <NTextarea
            v-model="form!.feedback"
            :assistive-text="errorMessages['feedback']"
            :invalid="!!errorMessages['feedback']"
            class="mb-4"
            data-test="feedback-message"
            variant="outline"
          />
        </template>
        <template #actions>
          <VButton
            variant="outlineSubtle"
            @click="close"
          >
            Cancel
          </VButton>
          <VButton
            variant="fill"
            :loading="loading"
            @click="submit"
          >
            Submit
          </VButton>
        </template>
      </VDialogCard>
    </VDialog>
  </div>
</template>
